import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { provideToastr } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { provideEcharts } from 'ngx-echarts';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { GoogleMapsModule } from '@angular/google-maps';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthService } from './core/services/auth.service';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { BranchContextService } from './core/services/branch-context.service';
import { environment } from './environments/environment';
import { MonitoringService } from './core/services/monitoring.service';

/**
 * Factory function for the APP_INITIALIZER.
 * It returns a function that returns the Promise from our AuthService.
 * @param authService The AuthService instance provided by Angular's DI.
 */
export function initializeAppFactory(
  authService: AuthService,
  branchContextService: BranchContextService
): () => Promise<void> {
  return async () => {
    // Check if we're on a callback route that shouldn't trigger full initialization
    const currentPath = window.location.pathname;
    const skipInitializationPaths = ['/google-cb', '/login', '/reset-password'];

    console.log('APP_INITIALIZER running for path:', currentPath);

    if (skipInitializationPaths.some(path => currentPath.includes(path))) {
      console.log('Skipping full initialization for callback/login route');
      return Promise.resolve();
    }

    console.log('Running full app initialization...');
    await authService.initialize();

    if (authService.currentUser()) {
      console.log('User authenticated, initializing branch context...');

      // IMPORTANT: Actually await the initialize() method
      await branchContextService.initialize();
      console.log('Branch context initialized successfully');
      console.log('Available branches:', branchContextService.availableBranches().length);
      console.log('Selected branch:', branchContextService.selectedBranchId());
    } else {
      console.log('No authenticated user, skipping branch context initialization');
    }

    console.log('App initialization complete');
  };
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    CommonModule,
    FormsModule,
    RouterModule,
    GoogleMapsModule
  ],
  providers: [
    provideEcharts(),
    provideAnimations(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      enableHtml: false,
      tapToDismiss: true,
    }),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    // Global error handler -> Sentry
    {
      provide: ErrorHandler,
      useClass: class GlobalErrorHandler implements ErrorHandler {
        handleError(error: any): void {
          MonitoringService.captureException(error, { location: 'GlobalErrorHandler' });
          // Preserve default behavior
          console.error(error);
        }
      }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [AuthService, BranchContextService], // Tell Angular to inject the AuthService into our factory
      multi: true,         // Required for APP_INITIALIZER as it can have multiple initializers
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
